import { DateTime } from 'luxon';

import type { Seconds } from 'tools/types';

export const capitalize = (str: string) => str[0].toUpperCase() + str.slice(1);

/** Restrict value between a lower and upper bound */
export const clamp = (num: number, min: number, max: number) =>
  Math.min(Math.max(num, min), max);

/** Forces a function to wait a certain amount of time before running again */
export const debounce = <T extends (...args: any) => any>(fn: T, wait = 0) => {
  let timer: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
};

export const formatBool = (isTrue: boolean) => (isTrue ? 'Yes' : 'No');

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const formatHideGrades = (hideGrades: string) => {
  if (hideGrades === '0') return 'None';
  if (hideGrades === '-1') return 'Yes';
  return `${hideGrades} ${pluralize('day', +hideGrades)}`;
};

export const formatNull = <T>(
  value: T | null,
  format: (value: T) => string = String,
) => (value === null ? '- -' : format(value));

export const formatNullish = <T>(
  value: T | null | undefined,
  format: (value: T) => string = String,
) => (value == null ? '- -' : format(value));

// time limit (in minutes)
export const formatTimeLimit = (timeLimit: Seconds) => {
  const hours = Math.floor(timeLimit / 3600);
  const minutes = Math.floor((timeLimit / 60) % 60);

  if (hours || minutes) {
    return `${hours.toLocaleString()} hr ${String(minutes).padStart(
      2,
      '0',
    )} min`;
  }

  return '- -';
};

// TODO Update types
// Ref: https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/lodash/common/object.d.ts#L1032
export const get = (from: any, selector: string) =>
  selector
    .replace(/\[([^[\]]*)\]/g, '.$1.')
    .split('.')
    .filter((t) => t !== '')
    .reduce((prev, cur) => prev && prev[cur], from);

export const getPercentage = (
  totalAwarded: number,
  totalPossible: number,
  options: { decimals?: number; roundDown?: boolean } = {
    decimals: 0,
    roundDown: false,
  },
) => {
  const overallRatio = totalPossible
    ? Math.min(totalAwarded / totalPossible, 1)
    : 0;
  return `${(options.roundDown ? roundDown : round)(
    overallRatio * 1e2,
    options.decimals,
  )}%`;
};

// calculate proportional stroke width for circular progress
// - the viewbox for circular progress is 22 by 22 so a thickness of 22
// - would mean a full circle, so we would just need to solve a ratio equation
// - x / viewbox = width / (size / 2)
export const getStrokeWidth = (width: number, size: number) => {
  const VIEWBOX_WIDTH = 22;
  return (VIEWBOX_WIDTH * width) / (size / 2);
};

export const loadScript = (src: string) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.defer = true;
    script.src = src;
    script.onload = resolve;
    script.onerror = (msg, url, lineNo, columnNo, err) => reject(err);
    document.getElementsByTagName('head')[0].appendChild(script);
  });

/** Filters out properties from an object */
export const omit = (obj: { [key: string]: any }, arr: string[]) =>
  Object.keys(obj)
    .filter((k) => !arr.includes(k))
    .reduce(
      (acc, key) => ((acc[key] = obj[key]), acc),
      {} as { [key: string]: any },
    );

export const pluralize = (word: string, number = 1) =>
  number === 1 ? word : `${word}s`;

export const round = (number: number, decimals = 0) => {
  const factor = Math.pow(10, decimals);
  return Math.round((number + Number.EPSILON) * factor) / factor;
};

export const roundDown = (number: number, decimals = 0) => {
  const factor = Math.pow(10, decimals);
  return Math.floor((number + Number.EPSILON) * factor) / factor;
};

/**
 * Truncates a number to a fixed number of decimal places without rounding,
 * avoiding IEEE-754 float precision issues.
 *
 * Uses String(value) which produces the shortest round-trip representation
 * of the float to avoid both multiplication-based underflow
 * (Math.floor(0.29 * 100) / 100 = 0.28) and toFixed(n) over-precision rounding
 * (toFixed(10) rounds 1.99999999999 up to 2).
 *
 * Edge cases:
 * - NaN / ±Infinity are returned unchanged.
 * - Negative numbers truncate toward zero (e.g. -1.999 -> -1.99).
 * - Values that String() renders in scientific notation (|value| < 1e-6 or
 *   |value| >= 1e21) are returned unchanged. These extremes either have no
 *   meaningful IEEE-754 fractional precision (large values) or are smaller
 *   than any typical truncation precision would produce (tiny values).
 *
 * @param value The number to truncate
 * @param decimals The number of decimal places to keep (default: 2, must be a non-negative integer)
 * @returns The truncated number
 * @throws RangeError if `decimals` is not a non-negative integer
 *
 * @example
 * truncateDecimals(99.999999)     // 99.99
 * truncateDecimals(0.29)          // 0.29  (not 0.28 as with Math.floor)
 * truncateDecimals(1.99999999999) // 1.99  (not 2 as with toFixed(10))
 * truncateDecimals(1e-7)          // 1e-7  (returned as-is)
 * truncateDecimals(1.5e21)        // 1.5e21 (returned as-is)
 */
export const truncateDecimals = (value: number, decimals = 2): number => {
  // decimals must be a non-negative integer; the operation is undefined otherwise
  if (!Number.isInteger(decimals) || decimals < 0) {
    throw new RangeError(
      `truncateDecimals: decimals must be a non-negative integer (received ${decimals})`,
    );
  }

  // If the value is not a finite number, return the original value
  if (!Number.isFinite(value)) return value;

  const str = String(value);

  // Values rendered in scientific notation (|value| < 1e-6 or >= 1e21)
  // are returned as-is — string slicing would mangle them.
  if (/e/i.test(str)) return value;

  const dotIndex = str.indexOf('.');

  // If there is no decimal point, return the original value
  if (dotIndex === -1) return value;

  const end = decimals === 0 ? dotIndex : dotIndex + decimals + 1;
  const result = parseFloat(str.slice(0, end));
  // Normalize -0 to +0 (e.g. truncating -1e-8 to 2 decimals)
  return result === 0 ? 0 : result;
};

export const snakeCase = (str: string) =>
  str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    ?.map((x) => x.toLowerCase())
    .join('_');

export const stripHTML = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const stripTrailingSlash = (url: string) =>
  url?.replace(/\/+$/, '') ?? url;

/**  Ensures that a function is called at most once in a specified time period */
export const throttle = <T extends (...args: any) => any>(fn: T, wait = 0) => {
  let inThrottle: boolean,
    lastFn: ReturnType<typeof setTimeout>,
    lastTime: number;
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      fn.apply(this, args);
      lastTime = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFn);
      lastFn = setTimeout(
        () => {
          if (Date.now() - lastTime >= wait) {
            fn.apply(this, args);
            lastTime = Date.now();
          }
        },
        Math.max(wait - (Date.now() - lastTime), 0),
      );
    }
  };
};

/** Returns array of unique elements that matches some criteria */
export const uniqBy = (arr: any[], key: string) => {
  const seen = new Set();
  return arr.filter((item) => {
    const k = item[key];
    if (seen.has(k)) {
      return false;
    }
    seen.add(k);
    return true;
  });
};

export const getDateWithoutTimezone = (date: string | null | undefined) => {
  if (!date || date === undefined || date === '' || date === null) return null;
  return date.split('T')[0];
};

// function that returns a date object according to local timezone

export const getLocalDate = (date: string | null | undefined) => {
  if (!date || date === undefined || date === '' || date === null) return null;

  const dateISO = getDateWithoutTimezone(date);

  if (!dateISO) return null;

  const newDate = DateTime.fromISO(dateISO).toJSDate();
  return newDate;
};

/**
 * Validates that a number has at most a specified number of decimal places.
 *
 * @param value - The number to validate
 * @param maxDecimalPlaces - The maximum number of decimal places allowed
 * @returns true if the number has valid decimal places (less than or equal to maxDecimalPlaces), false otherwise
 *
 * @example
 * hasValidDecimalPlaces(1.5, 2) // returns true
 * hasValidDecimalPlaces(1.555, 2) // returns false
 * hasValidDecimalPlaces(1, 2) // returns true (no decimal places)
 */
export const hasValidDecimalPlaces = (
  value: number,
  maxDecimalPlaces: number,
): boolean => {
  const [, decimalPart] = value.toString().split('.');
  return !decimalPart || decimalPart.length <= maxDecimalPlaces;
};
